import { FC, useEffect, useState } from 'react';
import Typography from '@ixigo/iui/Typography';
import Bottomsheet from '@ixigo/iui/Bottomsheet';
import { HistoryItem } from '../timelineTypes';
import { formatDateTime } from '../../../utils/dateFormatter';
import BottomsheetHeader from '../../BottomsheetHeader';
import ContentSection from '../ContentSection';
import StatusCard from './StatusCard';
import { TestId } from '../../../enums/test';

const COMMON_CLASSES = {
    bottomsheetContainer: 'flex flex-col bg-white rounded-t-[10px] overflow-hidden w-full h-full',
    separator: 'border-b border-gray-200',
    contentPadding: 'px-20 py-20',
    contentArea: 'flex-1 overflow-y-auto',
    sectionSpacing: 'mb-20',
};

type TimelineEntryDetailsProps = {
    item: HistoryItem | null;
    isOpen: boolean;
    onClose: () => void;
    resolutionMessage?: string;
    showThreadResolution?: boolean;
};

const TimelineEntryDetails: FC<TimelineEntryDetailsProps> = ({
    item,
    isOpen,
    onClose,
    resolutionMessage,
    showThreadResolution,
}) => {
    const [visible, setVisible] = useState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setVisible(true);
        }
    }, [isOpen]);

    const handleClose = () => {
        setVisible(false);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
            onClose();
        }, 300); // Match the transition duration
    };

    if (!item) return null;

    const { date, time } = formatDateTime(item.date);
    const isResolved = item.status === 'resolved';
    const shouldShowResolution = showThreadResolution ?? isResolved;

    return (
        <Bottomsheet open={visible} onClose={handleClose} classes={{ container: '!min-w-full !max-h-[80vh]' }}>
            <div className={COMMON_CLASSES.bottomsheetContainer} data-testid={TestId.TIMELINE_ENTRY_DETAILS}>
                <BottomsheetHeader
                    title={item.title}
                    subtitle={`${date} | ${time}`}
                    onClose={handleClose}
                    className='flex flex-col items-center pt-15 pb-15 bg-transparent relative border-none flex-shrink-0'
                />

                <div className={COMMON_CLASSES.separator} />

                <div className={`${COMMON_CLASSES.contentArea} ${COMMON_CLASSES.contentPadding}`}>
                    <ContentSection title='Your Query'>
                        <Typography variant='body-md' className='text-charcoal-800 leading-relaxed'>
                            {item.details?.threadSummary || item.summary}
                        </Typography>
                    </ContentSection>
                    {shouldShowResolution && (resolutionMessage || item.details?.threadResolution) && (
                        <ContentSection title='Resolution Provided' className='mt-[15px]'>
                            <Typography variant='body-md' className='text-charcoal-800 leading-relaxed'>
                                {resolutionMessage || item.details?.threadResolution}
                            </Typography>
                        </ContentSection>
                    )}

                    <div className='mt-15'>
                        <StatusCard isResolved={isResolved} message={item.details?.threadStatus || ''} />
                    </div>
                </div>
            </div>
        </Bottomsheet>
    );
};

export default TimelineEntryDetails;
